import { message } from "antd";
import axios from "axios";
import { errorActions } from "../helpers/auth_helper";
import {
  IIncomingStockParams,
  IIncomingStockResponse,
  IManualInventoryPayload,
  IStockPurchaseOrder,
  IStockPurchaseOrderDetail,
  IStockPurchaseOrderOptions,
  IStockPurchaseOrderParams,
  IVendorInventory,
  IVendorInventoryResponse,
} from "../interfaces/inventory.interface";

export const fetchVendorInventory = async (params: {
  page?: number;
  page_size?: number;
  search?: string;
}) => {
  try {
    const response = await axios.get<IVendorInventoryResponse>(
      `${process.env.REACT_APP_API_URL}/vendor-inventory-list/`,
      {
        params: {
          page: params.page || 1,
          page_size: params.page_size || 50,
          search: params.search || "",
        },
      }
    );
    return response.data;
  } catch (error: any) {
    errorActions(
      error,
      "Error fetching vendor inventory. Please check and try again"
    );
    return null;
  }
};

export const syncZohoInventory = async () => {
  try {
    const response = await axios.post(`${process.env.REACT_APP_API_URL}/zoho-inventory-sync/`, null, {
      params: {
        cronApiKey: "MdC50nJobs",
      },
    });
    message.success("Inventory synced successfully");
    return response.data;
  } catch (error: any) {
    errorActions(
      error,
      "Error syncing inventory. Please try again"
    );
    return null;
  }
};

// The manual-inventory endpoints return the standard envelope; on error the
// message sits at `error.message` (an object, which errorActions can't show).
const showManualInventoryError = (error: any, fallback: string) => {
  const body = error?.response?.data;
  message.error(body?.error?.message || fallback);
};

export const addManualInventory = async (payload: IManualInventoryPayload) => {
  try {
    const response = await axios.post<{ data: IVendorInventory }>(
      `${process.env.REACT_APP_API_URL}/vendor-inventory/manual/`,
      payload
    );
    message.success("Inventory added");
    return response.data.data;
  } catch (error: any) {
    showManualInventoryError(error, "Error adding inventory. Please try again");
    return null;
  }
};

export const updateManualInventory = async (id: number, quantity: number) => {
  try {
    const response = await axios.patch<{ data: IVendorInventory }>(
      `${process.env.REACT_APP_API_URL}/vendor-inventory/manual/${id}/`,
      { quantity }
    );
    message.success("Inventory updated");
    return response.data.data;
  } catch (error: any) {
    showManualInventoryError(error, "Error updating inventory. Please try again");
    return null;
  }
};

export const deleteManualInventory = async (id: number) => {
  try {
    await axios.delete(
      `${process.env.REACT_APP_API_URL}/vendor-inventory/manual/${id}/`
    );
    message.success("Inventory deleted");
    return true;
  } catch (error: any) {
    showManualInventoryError(error, "Error deleting inventory. Please try again");
    return false;
  }
};

// Items still owed on the POs raised in Zoho for our own stock, one row per unique item
// with its PO lines nested (the "Incoming Stock" tab).
export const fetchIncomingStock = async (params: IIncomingStockParams) => {
  try {
    const response = await axios.get<IIncomingStockResponse>(
      `${process.env.REACT_APP_API_URL}/zoho-inventory-purchase-orders/items/grouped/`,
      {
        params: {
          page: params.page || 1,
          page_size: params.page_size || 20,
          search: params.search || undefined,
          location_id: params.location_id?.length ? params.location_id.join(",") : undefined,
          zoho_vendor_id: params.zoho_vendor_id?.length ? params.zoho_vendor_id.join(",") : undefined,
          in_transit_only: params.in_transit_only || undefined,
          overdue: params.overdue || undefined,
          include_received: params.include_received || undefined,
          ordering: params.ordering || undefined,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    const body = error?.response?.data;
    message.error(body?.error?.message || "Error fetching incoming stock. Please try again");
    return null;
  }
};

const PO_BASE = `${process.env.REACT_APP_API_URL}/zoho-inventory-purchase-orders`;

const showEnvelopeError = (error: any, fallback: string) => {
  message.error(error?.response?.data?.error?.message || fallback);
};

// POs raised in Zoho for our own stock, one row per PO (the "Stock Purchase Orders" tab).
export const fetchStockPurchaseOrders = async (params: IStockPurchaseOrderParams) => {
  try {
    const csv = (v?: string[]) => (v?.length ? v.join(",") : undefined);
    const response = await axios.get<{
      data: IStockPurchaseOrder[];
      meta: { pagination: { count: number } };
    }>(`${PO_BASE}/`, {
      params: {
        page: params.page || 1,
        page_size: params.page_size || 20,
        search: params.search || undefined,
        received_status: csv(params.received_status),
        billed_status: csv(params.billed_status),
        zoho_vendor_id: params.zoho_vendor_id || undefined,
        branch_location_id: csv(params.branch_location_id),
        date_from: params.date_from || undefined,
        date_to: params.date_to || undefined,
        ordering: params.ordering || undefined,
      },
    });
    return response.data;
  } catch (error: any) {
    showEnvelopeError(error, "Error fetching purchase orders. Please try again");
    return null;
  }
};

export const fetchStockPurchaseOrderOptions = async () => {
  try {
    const response = await axios.get<{ data: IStockPurchaseOrderOptions }>(`${PO_BASE}/status-options/`);
    return response.data.data;
  } catch (error: any) {
    showEnvelopeError(error, "Error fetching purchase order filters");
    return null;
  }
};

export const fetchStockPurchaseOrderDetail = async (id: number) => {
  try {
    const response = await axios.get<{ data: IStockPurchaseOrderDetail }>(`${PO_BASE}/${id}/`);
    return response.data.data;
  } catch (error: any) {
    showEnvelopeError(error, "Error fetching purchase order items");
    return null;
  }
};
